import React, { useState } from "react";
import BulkUpload from "./BulkUpload";
import { camelCase } from "lodash";
import {
  validateFirstName,
  validateLastName,
  emailVaildation,
} from "../../../../_helpers/utilities";
import axiosInstance from "../../../../axios";
import { downloadCSV } from "../../../../_helpers/utilities";
import "./bulkUploadOverlay.scss";
import { fi } from "date-fns/locale";
import Modal from "../../../Other/Modal";
import { HelpCircle } from "lucide-react";

const { REACT_APP_LOGIN_API_BASEURL } = process.env;

const BulkUploadOverlay = ({ onCancel, role, setIsUploaded }) => {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState({ isUploading: false, text: "" });
  const [response, setResponse] = useState();
  const [showDemoModal, setShowDemoModal] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFile(file);
    uploadCSV(file);
  };

  const onDeleteFile = () => {
    setFile(null);
  };

  const responseType = response && response.success ? "success" : "failed";

  function parseCSV(str, delimiter = ",") {
    const headers = str.slice(0, str.indexOf("\n")).split(delimiter);
    const rows = str.slice(str.indexOf("\n") + 1).split("\n");

    const arr = rows.map(function (row) {
      const values = row.split(delimiter);
      try {
        const el = headers.reduce(function (object, header, index) {
          if (values[index]) {
            object[camelCase(header)] = values[index]
              ?.replace(/\r\n/g, "")
              .trim();
            return object;
          }
        }, {});
        return el;
      } catch (error) {
        setResponse({
          success: false,
          title: "Bulk Invite failed",
          message:
            "The bulk invite failed due to invalid entries. Kindly correct them and reupload to invite them.",
        });
        throw error;
      }
    });

    setProgress((state) => ({
      ...state,
      isUploading: true,
      progressCount: 30,
      text: "parsing and validating...",
    }));

    return arr.filter((item) => item);
  }

  const readCSV = async (file) => {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = function (e) {
        const records = parseCSV(e.target.result);
        return resolve({ records, file });
      };
      reader.onerror = function (error) {
        return reject(error);
      };
      reader.readAsText(file);
    });
  };

  const validateData = ({ records, file }) => {
    const validatedEntries = records.filter(
      (item) =>
        validateFirstName(item.firstName) &&
        validateLastName(item.lastName) &&
        emailVaildation(item.email)
    );

    const faultyEntries =
      records.filter((item) => validatedEntries.indexOf(item) == -1) || [];

    return {
      fileName: file.name,
      users: validatedEntries.map((item) => ({ ...item, role })),
      faultyEntries,
    };
  };

  const uploadData = async ({ faultyEntries, ...rest }) => {
    if (rest.users?.length > 0) {
      setProgress((state) => ({
        ...state,
        isUploading: true,
        progressCount: 90,
        text: "parsing and validating...",
      }));

      try {
        const { data } = await axiosInstance.post(
          `${REACT_APP_LOGIN_API_BASEURL}/upload-users`,
          rest
        );

        setProgress((state) => ({
          ...state,
          isUploading: true,
          progressCount: 100,
          text: "parsing and validating...",
        }));

        if (data.error) {
          setResponse({
            success: false,
            title: "An error has occurred",
            message:
              data.message || "The CSV file upload failed. Please try again uploading the file",
            faultyEntries,
          });
        } else {
          const allExisting = data.data.existingEmails?.length > 0 &&
            data.data.existingEmails?.length === rest.users?.length;

          if (allExisting) {
            setResponse({
              success: false,
              title: "An error has occurred",
              message: "All users already exist in the system",
              faultyEntries: [],
            });
          } else {
            if (!faultyEntries?.length) {
              setResponse({
                success: true,
                title: "Bulk invite sent successfully",
                message:
                  "The bulk invite processed successfully. You can view the status of the user in the dashboard.",
                faultyEntries,
              });
            } else {
              setResponse({
                success: true,
                title: "Bulk invite processed with some errors!",
                message: `The bulk invite has been processed, ${rest.users?.length} invites sent, ${faultyEntries?.length} invite(s) failed.`,
                faultyEntries,
              });
            }
            setIsUploaded((state) => !state);
          }
        }
      } catch (error) {
        console.error("Upload failed:", error);
        setResponse({
          success: false,
          title: "Server Error",
          message:
            "An unexpected error occurred while uploading the CSV. Please try again later.",
          faultyEntries,
        });
      }
    } else {
      setResponse({
        success: false,
        title: "Bulk Invite failed",
        message:
          "The bulk invite failed due to invalid entries. Here is the CSV file for you which has failed invites. Kindly correct them and reupload to invite them.",
        faultyEntries,
      });
    }
  };


  const uploadCSV = (file) => {
    setProgress((state) => ({
      ...state,
      isUploading: true,
      progressCount: 1,
      text: "parsing and validating...",
    }));
    //console.log("file", file);
    if (
      file.type === "text/csv" ||
      file.type === "application/vnd.ms-excel" ||
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      readCSV(file).then(validateData).then(uploadData);
    } else {
      setResponse({
        success: false,
        title: "Invalid file!",
        message: "Please use CSV format for bulk upload",
        faultyEntries: [],
      });
    }
  };

  const toPascalCase = (str) => {
    return (str.match(/[a-zA-Z0-9]+/g) || [])
      .map((w) => `${w.charAt(0).toUpperCase()}${w.slice(1)}`)
      .join("");
  };

  const handleDownloadCSV = (e) => {
    e.preventDefault();
    //console.log("test");
    const csvContent =
      `${Object.keys(response.faultyEntries[0])
        .map((item) => toPascalCase(item))
        .join(",") + "\n"
      }` +
      response.faultyEntries
        .map((item) => Object.values(item).join(","))
        .join("\n");
    downloadCSV(csvContent, "faulty-data");
  };

  const handleDownloadCSVTemplate = (e) => {
    e.preventDefault();
    const headers = [
      "FirstName",
      "LastName",
      "UserName",
      "Email",
      "Password",
      "PhoneNumber",
      "CountryCode",
      "State"
    ];
    const csvContent = headers.join(",") + "\n";
    downloadCSV(csvContent, "template");
  };

  return (
    <div className="forms-width-55 add-instructor bulk-wrapper">
      <div className="dashboard-container-new padding-custom-dashboard-0">
        <p>Bulk Upload Users</p>
      </div>

      <div className="">
        <div className="">
          <div className="margin-top-new-01 margin-r-n">
            <div className="box-shadow-new-01">
              {response && (
                <div className="upload-response">
                  <div className={responseType}></div>
                  <div className="response-title">{response.title}</div>
                  <div className="description">
                    {response?.message}
                    {response?.faultyEntries?.length > 0 && (
                      <p className="download-csv">
                        The bulk invite failed due to invalid entries. Here is
                        the <a onClick={handleDownloadCSV}>CSV file</a> for you
                        which has failed invites. Kindly correct them and
                        reupload to invite them.{" "}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {!response && (
                <>
                  <div className="title">Choose file</div>
                  <div className="description">
                    To add and invite learners,{" "}
                    <a
                      href="#"
                      className="template"
                      onClick={handleDownloadCSVTemplate}
                    >
                      download the .csv{" "}
                    </a>
                    or check the{" "}
                    <button
                      type="button"
                      onClick={() => setShowDemoModal(true)}
                      className="text-slate-800 hover:text-slate-900 font-bold underline inline-flex items-center gap-1 cursor-pointer transition-all border-none bg-transparent p-0"
                    >
                      Demo Structure
                    </button>
                  </div>

                  <div className="upload-container">
                    <div className="upload-drop">
                      <div className="course_drop-message">
                        <div className="course_upload-icon"></div>
                        <span className="course_image-drop-text">
                          Drag and drop or browse to choose a file
                        </span>
                      </div>
                      <input
                        className="upload-file"
                        type="file"
                        onChange={handleFileChange}
                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      />
                    </div>
                  </div>
                  {file && progress.isUploading && (
                    <BulkUpload
                      file={file}
                      progress={progress}
                      handleDeleteFile={onDeleteFile}
                    />
                  )}
                </>
              )}
              <div className="dashboard-main justify-flex-new">
                <div className="margin-top-new-01-0">
                  <button
                    className="lastmonth-btn-0"
                    onClick={() => onCancel((state) => !state)}
                  >
                    {response ? "Close" : "Cancel"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demo structure modal popup */}
      {showDemoModal && (
        <Modal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)}>
          <div className="p-6 max-w-md w-full font-sans" style={{ minWidth: "350px" }}>
            <h3 className="text-lg font-bold text-slate-800 border-b border-slate-100 pb-3 mb-4 flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-slate-500" /> Required CSV Format
            </h3>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              Please ensure your CSV file contains these headers exactly as shown, and all columns are filled with valid info:
            </p>
            <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white shadow-3xs mb-4">
              <table className="w-full text-left text-[11px] border-collapse whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 font-bold text-slate-700">
                    <th className="py-2.5 px-3">FirstName</th>
                    <th className="py-2.5 px-3">LastName</th>
                    <th className="py-2.5 px-3">UserName</th>
                    <th className="py-2.5 px-3">Email</th>
                    <th className="py-2.5 px-3">Password</th>
                    <th className="py-2.5 px-3">PhoneNumber</th>
                    <th className="py-2.5 px-3">CountryCode</th>
                    <th className="py-2.5 px-3">State</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-600 font-semibold">
                  <tr>
                    <td className="py-2.5 px-3">John</td>
                    <td className="py-2.5 px-3">Doe</td>
                    <td className="py-2.5 px-3">johndoe</td>
                    <td className="py-2.5 px-3 text-slate-500 font-normal">john.doe@example.com</td>
                    <td className="py-2.5 px-3 font-mono text-[10px]">Pass123!</td>
                    <td className="py-2.5 px-3 font-mono text-[10px]">1234567890</td>
                    <td className="py-2.5 px-3">US</td>
                    <td className="py-2.5 px-3">California</td>
                  </tr>
                  <tr>
                    <td className="py-2.5 px-3">Jane</td>
                    <td className="py-2.5 px-3">Smith</td>
                    <td className="py-2.5 px-3">janesmith</td>
                    <td className="py-2.5 px-3 text-slate-500 font-normal">jane.smith@example.com</td>
                    <td className="py-2.5 px-3 font-mono text-[10px]">Secure456#</td>
                    <td className="py-2.5 px-3 font-mono text-[10px]">9876543210</td>
                    <td className="py-2.5 px-3">GB</td>
                    <td className="py-2.5 px-3">London</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-amber-50 border border-amber-200/80 rounded-lg p-3 text-[10px] text-amber-800 leading-normal mb-5">
              <strong>Important:</strong> Header names are case-sensitive and must be spelled exactly as shown. Duplicate or existing emails will be skipped.
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowDemoModal(false)}
                className="px-4 py-2 text-xs font-semibold text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-all cursor-pointer shadow-3xs"
              >
                Got it
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default BulkUploadOverlay;
